// 送信できなかった events を localStorage に退避し、次回セッションで送り直す
// (spec 2026-07-29 §3.4)。
//
// read-modify-write はこのファイルの中だけで行い、呼び出し側に localStorage を
// 触らせない。複数箇所から読んで書くと lost update になる。
//
// 再送先が events.jsonl ではなく events-recovered-{stamp}.jsonl なのが要点:
// events endpoint は PUT も POST も全上書きなので、同じキーに再送すると
// 既に届いていた分 (beacon 由来の session.ended 等) を消してしまう。
// しかも sendBeacon の戻り値は「キューに入った」しか意味せず、届いたかは
// 原理的に判らないので「届いていたら再送しない」判断ができない (§3.4.1)。

// 判定の値だけを持つ層から取る。connectivityStore (window / document / fetch に
// 触る側) を import してはいけない — このファイルは tsconfig.functions.json
// (DOM の無いプロジェクト) からも型検査される (詳細は connectivityVerdict.ts の冒頭)。
import { isConfirmedOffline } from "../state/connectivityVerdict";

const KEY = "audio-form:pending-uploads:v1";
const MAX_TOTAL_BYTES = 256 * 1024;
const MAX_ENTRY_BYTES = 64 * 1024;
const MAX_ENTRIES = 5;
export const MAX_ATTEMPTS = 3;

export type PendingUpload = {
  sessionId: string;
  userId: string;
  jsonl: string;
  /** ISO8601。stamp の元 (D7) */
  failedAt: string;
  attempts: number;
};

/**
 * "2026-07-29T13:42:16.789Z" → "20260729T134216789Z"
 * 決定的なので、同じ退避を 2 回送っても同じキーに上書きされ冪等になる (D7)。
 * ミリ秒精度で衝突せず、辞書順 = 時刻順。
 */
export function stampFromFailedAt(failedAt: string): string {
  return failedAt.replace(/[-:.]/g, "");
}

function byteLength(text: string): number {
  return new Blob([text]).size;
}

/**
 * 末尾の行から落として MAX_ENTRY_BYTES に収める。
 * エントリ単位の trim が無いと、イベントが多いセッション = 最も知りたい
 * セッションほど合計上限に弾かれて黙って退避されない。
 */
function trimJsonl(jsonl: string): string {
  if (byteLength(jsonl) <= MAX_ENTRY_BYTES) return jsonl;
  const lines = jsonl.split("\n");
  while (lines.length > 1 && byteLength(lines.join("\n")) > MAX_ENTRY_BYTES) {
    lines.pop();
  }
  return lines.join("\n");
}

function isPendingUpload(value: unknown): value is PendingUpload {
  if (!value || typeof value !== "object") return false;
  const e = value as Record<string, unknown>;
  return (
    typeof e.sessionId === "string" &&
    typeof e.userId === "string" &&
    typeof e.jsonl === "string" &&
    typeof e.failedAt === "string" &&
    typeof e.attempts === "number"
  );
}

function read(): PendingUpload[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isPendingUpload);
  } catch {
    // 壊れた退避データは捨てる (既存 loadCompletion と同じ方針)
    return [];
  }
}

function write(entries: PendingUpload[]): void {
  try {
    let list = entries.slice(-MAX_ENTRIES);
    // 合計上限は古い方から落として収める
    while (list.length > 1 && byteLength(JSON.stringify(list)) > MAX_TOTAL_BYTES) {
      list = list.slice(1);
    }
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* quota / private mode は諦める (原則 5) */
  }
}

export function loadPendingUploads(): PendingUpload[] {
  return read();
}

/**
 * 同一 sessionId は 1 エントリに畳む (最新で置換)。
 * 畳まないと 1 セッションの snapshot 連続失敗だけで MAX_ENTRIES を
 * 使い切り、別セッションの退避を追い出す。
 */
export function savePendingUpload(entry: {
  sessionId: string;
  userId: string;
  jsonl: string;
  failedAt: string;
}): void {
  const rest = read().filter((e) => e.sessionId !== entry.sessionId);
  rest.push({ ...entry, jsonl: trimJsonl(entry.jsonl), attempts: 0 });
  write(rest);
}

export function clearPendingUploads(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* 使えない環境では諦める */
  }
}

/**
 * 退避した events を送り直す。App の bootstrap から fire-and-forget で呼ぶ。
 *
 * 1 件ずつ順に送るのは意図的 — 同時多発 PUT にすると per-IP rate limit
 * (functions/_middleware.ts の RATE_LIMIT_SESSIONS) を自分で踏む。
 * 429 が元の失敗原因だった場合、並列送信は状況を悪化させる (§3.5.3)。
 */
export async function flushPendingUploads(
  opts: { fetch?: typeof fetch; isOnline?: () => boolean } = {}
): Promise<void> {
  try {
    const fetchImpl = opts.fetch ?? ((...args: Parameters<typeof fetch>) => fetch(...args));
    // 判定はバナーと同じものを使う (原則 4)。生の navigator.onLine を読むと、
    // それが誤って false で固着した端末では**バナーが誤って出るのと同時に**、
    // 前回セッションで送れなかったログの再送まで黙って止まる (2026-08-23 の実機)。
    // checking (判定中) では送りにいく — 確信を持ってオフラインのときだけ飛ばす。
    const isOnline = opts.isOnline ?? (() => !isConfirmedOffline());

    // オフラインなら「ネットワークに触れていない」ので attempts を消費しない。
    // ここを飛ばすと attempts が「起動回数」を数えることになり、オフラインの
    // まま 3 回開いただけで退避が捨てられる。
    if (!isOnline()) return;

    const entries = read();
    if (entries.length === 0) return;

    const survivors: PendingUpload[] = [];
    for (const e of entries) {
      let ok = false;
      // サーバが応答したか。fetch の throw (回線断・DNS 失敗・接続ハング) は
      // 1 往復もしていないので、gate と同じ理由で attempts を消費しない。
      // ここを分けないと、判定が checking のまま 3 回起動しただけで退避が捨てられる。
      let reached = false;
      try {
        const stamp = stampFromFailedAt(e.failedAt);
        const url =
          `/api/sessions/${encodeURIComponent(e.sessionId)}/events-recovered` +
          `?userId=${encodeURIComponent(e.userId)}&stamp=${stamp}`;
        const res = await fetchImpl(url, {
          method: "PUT",
          headers: { "content-type": "application/x-ndjson" },
          body: e.jsonl,
        });
        reached = true;
        ok = res.ok;
      } catch {
        ok = false;
      }
      if (ok) continue;
      if (!reached) {
        survivors.push(e);
        continue;
      }
      const attempts = e.attempts + 1;
      if (attempts < MAX_ATTEMPTS) survivors.push({ ...e, attempts });
    }
    write(survivors);
  } catch {
    /* 再送は best-effort。bootstrap を止めない (原則 5) */
  }
}
